<template>
    <app-layout
        :hasChanges="form.isDirty"
    >
        <form @submit.prevent="submit">
            <page-header>
                <template #title>Edit Order</template>

                <template #actions>
                        <jet-button v-show="form.isDirty" type="submit" class="leading-none" :disabled="form.processing || !this.canPlaceOrder" >
                            Save
                        </jet-button>

                        <jet-tertiary-button
                            v-if="form.isDirty"
                            :disabled="form.processing"
                            class="leading-none"
                            @click="showUnsavedChangesModal = true"
                        >
                            Cancel
                        </jet-tertiary-button>

                        <jet-tertiary-button
                            v-else
                            :disabled="form.processing"
                            :href="route('customer_orders.index')"
                            class="leading-none"
                        >
                            Close
                        </jet-tertiary-button>
                    </template>
            </page-header>

            <div class="px-8">
                <div class="flex gap-x-12">
                    <div class="w-1/3">
                        <div class="flex items-center mb-2">
                            <jet-label for="order_date" value="Order #:" class="w-32 mb-0 leading-7" />
                            <p>{{ order.id }}</p>
                        </div>

                        <div class="flex items-center mb-2">
                            <jet-label for="order_date" value="Order Date:" class="w-32 mb-0 leading-7" />
                            <p>{{ order.order_date }}</p>
                        </div>

                        <div class="flex items-center mb-2">
                            <jet-label for="shipping_method" value="Shipping Method:" class="w-32 mb-0 leading-7 whitespace-nowrap" />
                            <p class="capitalize">{{ order.shipping_method }}</p>
                        </div>

                        <div class="flex items-center">
                            <jet-label for="ship_date" value="Ship Date:" class="w-32 mb-0 leading-7 self-start" />
                            <div class="flex flex-1 flex-col">
                                <datepicker inputClassName="dp-custom-input"
                                    v-model="form.ship_date"
                                    weekStart="0"
                                    monthNameFormat="long"
                                    :enableTimePicker="false"
                                    :autoApply="true"
                                    :is24="false"
                                    :clearable="false"
                                    :minDate="minDate"
                                    :maxDate="maxDate"
                                    :disabled-dates="customerDisabledDates"
                                    :format="formatDate"
                                />
                                <jet-input-error :message="$page.props.errors.ship_date" class="m-1" />
                            </div>
                        </div>


                    </div>

                    <div class="w-1/3">
                        <div class="flex flex-1 flex-col gap-y-2">
                            <div class="flex items-center">
                                <jet-label for="order_date" value="Customer:" class="w-32 xl:w-28 mb-0 leading-7" />
                                <p>{{ activeCustomer.name }}</p>
                            </div>

                            <div class="flex flex-1 items-start">
                                <jet-label for="address" value="Address:" class="w-32 xl:w-28 mb-0 leading-7" />
                                <div v-if="activeCustomer" class="leading-tight">
                                    <div>{{ activeCustomer.address_1 }}</div>
                                    <div>{{ activeCustomer.address_2 }}</div>
                                    <div>{{ activeCustomer.city }} {{ activeCustomer.province }}, {{ activeCustomer.postal_code }}</div>
                                </div>
                            </div>

                            
                            <div class="flex mb-2">
                                <jet-label value="PO #:" class="w-32 xl:w-20 align-top leading-7" />

                                <div class="flex-1">
                                    <div v-if="order.status === 2">
                                        {{ form.po_num }}
                                    </div>
                                    <jet-input v-else type="text" v-model="form.po_num" class="w-full"/>
                                    <jet-input-error :message="$page.props.errors.po_num" class="mt-1" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="w-1/3">
                        <div class="flex items-start">
                            <jet-label for="notes" value="Shipping Notes:" class="w-32 xl:w-28 mb-0 leading-7" />
                            <div class="flex flex-1">
                                <textarea class="w-full resize-y" v-model="form.notes" style="min-height: 98px;"></textarea>
                            </div>
                        </div>
                    </div>
                </div>

                <order-items-edit class="my-4"
                    :order="order"
                    :form="form"
                    :customers="[activeCustomer]"
                    :products="products"
                    :categories="categories"
                    :is_wholesale="order.is_wholesale"
                    :orderMinimum="orderMinimum"
                    :deliveryFeeBelowMinimum="deliveryFeeBelowMinimum"
                    :skipsDeliveryMinimumRules="skipsDeliveryMinimumRules"
                    @updateCanPlaceOrder="updateCanPlaceOrder"
                />
            </div>
        </form>

        <div class="text-center py-24" >
            <p v-if="order.status === -1" class="text-2xl text-red-700 opacity-50">--- Cancelled ---</p>

            <jet-danger-button @click="showCancelModal = true" v-else>
                Cancel Order
            </jet-danger-button>
        </div>

        <unsaved-changes-modal-2
            :formRoute="formRoute"
            :isDirty="form.isDirty"
            :show="showUnsavedChangesModal"
            @close="showUnsavedChangesModal = false"
            @confirm="$inertia.get(route('customer_orders.index'))"
        />

        <jet-confirmation-modal :show="showCancelModal" @close="showCancelModal = false">
            <template #title>
                Cancel Order
            </template>

            <template #content>
                Are you sure you would like to cancel this order?
            </template>

            <template #footer>
                <jet-danger-button @click="cancel" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                    Yes
                </jet-danger-button>

                <jet-tertiary-button class="ml-2" @click="showCancelModal = false">
                    No
                </jet-tertiary-button>
            </template>
        </jet-confirmation-modal>
    </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetButton from '@/Jetstream/Button';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetInputError from '@/Jetstream/InputError';
import JetInput from '@/Jetstream/Input';
import JetLabel from '@/Jetstream/Label';
import OrderItemsEdit from '@/Shared/Orders/OrderItemsEdit';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import UnsavedChangesModal2 from '@/Shared/UnsavedChangesModal2';
import Datepicker from '@vuepic/vue-datepicker';
import { DateTime } from 'luxon';
import Multiselect from '@vueform/multiselect';
import { fetchCustomerOrderEditShipDateConstraints } from '@/Composables/useWholesaleShipDateConstraints';

import '@vueform/multiselect/themes/default.css';
import '@vuepic/vue-datepicker/dist/main.css';

export default {
    props: ['order', 'products', 'categories', 'holidays', 'orderMinimum', 'deliveryFeeBelowMinimum', 'skipsDeliveryMinimumRules'],

    setup(props) {
        const form = useForm({
            id: props.order.id,
            customer_id: props.order.customer_id,
            allow_substitutions: props.order.allow_substitutions,
            ship_date: new Date(props.order.ship_date + ' EST'),
            po_num: props.order.po_num,
            shipping_method: props.order.shipping_method,
            delivery_fee: props.order.delivery_fee,
            applied_credit_amount: props.order.applied_credit_amount,
            notes: props.order.notes,
            order_items: props.order.order_items.map(i => {
                return {
                    id: i.id,
                    order_id: i.order_id,
                    promo_code_id: i.promo_code_id,
                    product_id: i.product_id,
                    substitution: i.substitution,
                    title: i.title,
                    description: i.description,
                    product_notes: i.product_notes,
                    size: i.size,
                    tax: i.tax,
                    partial_qty: i.partial_qty,
                    cost: i.cost,
                    price: i.price,
                    quantity: i.quantity,
                    orig_quantity: i.orig_quantity,
                    product: i.product,
                }
            })
        })

        return { form }
    },

    components: {
        AppLayout,
        PageHeader,
        JetButton,
        JetTertiaryButton,
        JetDangerButton,
        JetInputError,
        JetLabel,
        JetInput,
        OrderItemsEdit,
        JetConfirmationModal,
        UnsavedChangesModal2,
        Datepicker,
        DateTime,
        Multiselect,
    },

    data() {
        return {
            showCancelModal: false,
            showUnsavedChangesModal: false,
            formRoute: this.route('customer_orders.update', this.order.id),

            shippingOptions: [
                { value: 'delivery', label: 'Delivery' },
                { value: 'pickup', label: 'Pickup' },
            ],

            canPlaceOrder: false,
            shipDateConstraints: null,
            shipDateRefetchTimer: null,
        }
    },

    computed: {
        minDate () {
            if (this.shipDateConstraints?.min_date) {
                return DateTime.fromISO(this.shipDateConstraints.min_date).toJSDate();
            }
            return DateTime.now().toJSDate();
        },

        maxDate () {
            if (this.shipDateConstraints?.max_date) {
                return DateTime.fromISO(this.shipDateConstraints.max_date).toJSDate();
            }
            let today = new Date();
            return new Date(today.setMonth(today.getMonth() + 3));
        },

        customerDisabledDates () {
            if (!this.activeCustomer || !this.shipDateConstraints?.disabled_dates) {
                return [];
            }
            return this.shipDateConstraints.disabled_dates.map((d) => DateTime.fromISO(d).toJSDate());
        },

        dairyCategory() {
            let category = this.categories.find(c => c.id == 96);

            if(category === undefined) return null;

            return category.id;
        },

        hasSpecialOrderItems() {
            return this.form.order_items.filter(i => i.product.special_order === true).length > 0;
        },

        hasDairyItems() {
            if(!this.dairyCategory) return false;

            return this.form.order_items.filter(i => i.product.category_id === this.dairyCategory).length > 0;
        },

        activeCustomer() {
            return this.$page.props.activeCustomer;
        },
    },

    watch: {
        'form.order_items': {
            handler() {
                this.scheduleShipDateRefetch();
            },
            deep: true,
        },
    },

    mounted() {
        this.scheduleShipDateRefetch();
    },

    methods: {
        scheduleShipDateRefetch() {
            if (this.shipDateRefetchTimer) {
                clearTimeout(this.shipDateRefetchTimer);
            }
            this.shipDateRefetchTimer = setTimeout(() => {
                this.refetchShipDateConstraints();
            }, 200);
        },

        async refetchShipDateConstraints() {
            if (!this.activeCustomer) {
                return;
            }
            try {
                const orderItems = (this.form.order_items || []).map((i) => ({
                    product_id: i.product_id,
                }));
                this.shipDateConstraints = await fetchCustomerOrderEditShipDateConstraints(this.order.id, {
                    shipping_method: this.form.shipping_method,
                    order_items: orderItems,
                });
            } catch (e) {
                console.error(e);
            }
        },

        submit () {
            if (!this.form.processing) {
                this.form
                    .transform((data) => ({
                        ...data,
                        ship_date: data.ship_date ? this.formatDate(data.ship_date) : null,
                        order_items: data.order_items.map(({ customer_price, customer_split_case_price, last_quantity_for_price, ...item }) => item),
                    }))
                    .submit('put', route('customer_orders.update', this.order.id));
            }
        },

        cancel () {
            if(!this.form.processing) this.form.delete(route('customer_orders.destroy', this.order.id));
        },

        formatDate(date) {
            return DateTime.fromJSDate(date).toFormat('yyyy-LL-dd');
        },

        updateCanPlaceOrder(value) {
            this.canPlaceOrder = value;
        },
    }
}
</script>

<style>
.dp-custom-input {
    @apply h-7;
}
</style>
